import React from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import {
  Server, KeyRound, Database, Shield, Terminal, GitBranch, Cloud, Mail, Bot,
  CheckCircle2, ExternalLink,
} from "lucide-react";

/**
 * /docs/setup — production setup and operator guide.
 *
 * Intent: anyone deploying ArgusSecure (self-host or operating on a managed
 * Emergent build) should be able to read this page top-to-bottom and have a
 * green health endpoint in under an hour. No fictional vendors. No hype.
 */

const EnvVar = ({ name, required, purpose }) => (
  <tr className="border-t border-line">
    <td className="py-2 pr-4 font-mono text-xs text-gold whitespace-nowrap align-top">{name}</td>
    <td className="py-2 pr-4 align-top">
      <span
        className={
          required
            ? "text-[10px] uppercase tracking-wider text-sev-critical bg-sev-critical/10 border border-sev-critical/30 rounded-full px-2 py-0.5"
            : "text-[10px] uppercase tracking-wider text-ink/60 border border-line rounded-full px-2 py-0.5"
        }
      >
        {required ? "required" : "optional"}
      </span>
    </td>
    <td className="py-2 text-sm text-ink/75 leading-relaxed">{purpose}</td>
  </tr>
);

const Section = ({ id, Icon, title, children }) => (
  <section id={id} className="border-t border-line py-10" data-testid={`docs-section-${id}`}>
    <div className="flex items-center gap-3 mb-4">
      <Icon size={18} className="text-gold" />
      <h2 className="font-display text-2xl text-ink">{title}</h2>
    </div>
    <div className="prose prose-invert max-w-none text-ink/80 leading-relaxed text-[15px] space-y-4">
      {children}
    </div>
  </section>
);

const Code = ({ children }) => (
  <pre className="rounded-lg border border-line bg-paper p-4 text-xs font-mono text-ink/85 overflow-x-auto" data-testid="docs-code-block">
    <code>{children}</code>
  </pre>
);

export default function DocsSetup() {
  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col" data-testid="docs-setup-page">
      <MarketingHeader />

      <main className="flex-1">
        {/* HERO */}
        <section className="max-w-4xl mx-auto px-6 pt-20 pb-10 w-full">
          <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Operator guide</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
            Setup &amp; <em className="text-gold">deployment.</em>
          </h1>
          <p className="text-ink/70 mt-5 max-w-2xl text-base lg:text-lg leading-relaxed">
            How to stand ArgusSecure up — locally, on Emergent, or on your own infra.
            Every secret, every env var, every health check. Nothing handwaved.
          </p>

          <nav className="mt-8 flex flex-wrap gap-2 text-sm" aria-label="On this page" data-testid="docs-toc">
            {[
              ["Architecture", "architecture"],
              ["Scanner coverage", "scanners"],
              ["Quick start", "quick-start"],
              ["Environment variables", "env-vars"],
              ["Health checks", "health"],
              ["Integrations", "integrations"],
              ["Production checklist", "prod-checklist"],
              ["Troubleshooting", "troubleshooting"],
            ].map(([label, id]) => (
              <a
                key={id}
                href={`#${id}`}
                className="rounded-full border border-line bg-panel px-3 py-1.5 text-ink/70 hover:border-gold hover:text-gold transition-colors"
                data-testid={`docs-toc-${id}`}
              >
                {label}
              </a>
            ))}
          </nav>
        </section>

        <div className="max-w-4xl mx-auto px-6 pb-20 w-full">
          {/* ARCHITECTURE */}
          <Section id="architecture" Icon={Server} title="Architecture at a glance">
            <p>
              ArgusSecure is a three-tier app: React frontend (port <code>3000</code>),
              FastAPI backend (port <code>8001</code>) under <code>/api</code> ingress,
              and MongoDB for persistence. Long-running scans run as FastAPI
              <code>BackgroundTasks</code>; OWASP ZAP is reached via the official
              <code>zaproxy</code> client when a project is configured to use it.
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Stateless API</strong> — every JWT contains <code>jti</code>; logout writes to <code>revoked_tokens</code>.</li>
              <li><strong>Auth</strong> — argon2id hashes via passlib. JWT secret rotated by setting <code>JWT_SECRET</code>.</li>
              <li><strong>Rate limits</strong> — MongoDB-backed sliding window, applied per route via <code>rate_limit()</code>.</li>
              <li><strong>Secrets</strong> — every credential read with <code>os.environ.get()</code>. Never hardcoded.</li>
            </ul>
          </Section>

          {/* SCANNER COVERAGE — embedded vs subprocess */}
          <Section id="scanners" Icon={Shield} title="Embedded scanner coverage">
            <p>
              Every scan mode has a built-in implementation. ArgusSecure ships
              its own scanners for SAST, DAST, SCA, and Cloud, and bundles
              additional OSS engines (Semgrep, Trivy, Gitleaks, Checkov,
              Nuclei, Grype) so secrets, IaC, and container modes work out of
              the box too. Commercial connectors are optional — never required
              for any mode.
            </p>
            <div className="rounded-xl border border-line bg-panel p-4 overflow-x-auto" data-testid="docs-scanner-matrix">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-ink/45">
                    <th className="pb-2 pr-4">Mode</th>
                    <th className="pb-2 pr-4">Built-in (always available)</th>
                    <th className="pb-2 pr-4">Bundled OSS engines</th>
                    <th className="pb-2">Optional connectors</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-xs">
                  {[
                    ["SAST", "Regex + Python AST + intra-procedural taint (JS / TS / Python / Go / Java)", "Semgrep", "CodeQL, SonarQube, Snyk Code, Checkmarx, Veracode"],
                    ["DAST", "Passive / Standard / Deep profiles. SSRF-safe. OWASP Top 10:2025 mapped.", "OWASP ZAP, Nuclei", "Burp Enterprise, StackHawk, Acunetix"],
                    ["SCA", "Curated 16-CVE seed + OSV.dev live feed augmentation (npm, PyPI)", "Trivy fs", "Snyk Open Source, OWASP Dependency-Check"],
                    ["Secrets", "Built-in patterns: AWS keys, GitHub PAT, Slack, Stripe, Google API, PEM blocks", "Gitleaks", "—"],
                    ["IaC", "—", "Checkov (Terraform, K8s, Dockerfile, CloudFormation, Helm)", "Snyk IaC, Checkmarx IaC"],
                    ["Container", "—", "Trivy image (CVE scan across base + app layers)", "Snyk Container, Grype"],
                    ["Cloud", "AWS S3 public-bucket anonymous probe", "—", "(coming soon)"],
                  ].map(([mode, builtin, oss, connectors]) => (
                    <tr key={mode} className="border-t border-line align-top">
                      <td className="py-2 pr-4 font-semibold text-gold">{mode}</td>
                      <td className="py-2 pr-4 text-ink/80">{builtin}</td>
                      <td className="py-2 pr-4 text-ink/80">{oss}</td>
                      <td className="py-2 text-ink/55">{connectors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-ink/55">
              Bundled OSS binaries (Semgrep, Trivy, Gitleaks, Checkov, Nuclei, Grype) are stashed
              under <code>/app/backend/_bin/</code> and re-linked into <code>/usr/local/bin/</code> on
              every backend boot. View live status under <em>Admin → Engines</em>.
            </p>
          </Section>

          {/* QUICK START */}
          <Section id="quick-start" Icon={Terminal} title="Local quick start">
            <p>Clone, install, run. The frontend hot-reloads; the backend hot-reloads via supervisor.</p>
            <Code>{`# 1. Backend
cd backend
pip install -r requirements.txt
cp .env.example .env   # fill in MONGO_URL, DB_NAME at minimum

# 2. Frontend
cd ../frontend
yarn install
cp .env.example .env   # set REACT_APP_BACKEND_URL

# 3. Run (supervisor manages both)
sudo supervisorctl restart backend frontend
curl -s $REACT_APP_BACKEND_URL/api/health   # → {"status":"ok",...}`}</Code>
            <p>
              The seed runs on backend startup and provisions a demo organization
              with three pre-scanned projects (SAST/SCA/DAST). Default credentials
              are listed below — change them before any production deploy.
            </p>
          </Section>

          {/* ENV VARS */}
          <Section id="env-vars" Icon={KeyRound} title="Environment variables">
            <p>The two <code>.env</code> files map cleanly onto frontend and backend processes.</p>

            <h3 className="font-display text-xl text-ink mt-6 mb-2">backend/.env</h3>
            <div className="rounded-xl border border-line bg-panel p-4 overflow-x-auto" data-testid="docs-env-backend">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-ink/45">
                    <th className="pb-2 pr-4">Variable</th>
                    <th className="pb-2 pr-4">Status</th>
                    <th className="pb-2">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <EnvVar name="MONGO_URL" required purpose="Mongo connection string. e.g. mongodb://localhost:27017" />
                  <EnvVar name="DB_NAME" required purpose="Logical database name — typically argussecure." />
                  <EnvVar name="JWT_SECRET" required purpose="32+ random bytes. Rotating this invalidates every active session." />
                  <EnvVar name="EMERGENT_LLM_KEY" purpose="Powers Goddy and the AI Fix Agent (Claude Sonnet 4.5). Without it, the chat returns a graceful disabled-state." />
                  <EnvVar name="STRIPE_API_KEY" purpose="Billing checkout. Optional in dev — the platform also accepts the Emergent test proxy key." />
                  <EnvVar name="RESEND_API_KEY" purpose="Outbound transactional email. Without it, mail is queued in the DB outbox." />
                  <EnvVar name="HCAPTCHA_SECRET" purpose="hCaptcha server secret. Without it, captcha is a no-op (recommended only for local dev)." />
                  <EnvVar name="HCAPTCHA_SITE_KEY" purpose="Public key returned to the frontend so it can render the widget." />
                  <EnvVar name="CORS_ORIGINS" purpose="Comma-separated list of additional allowed origins. Defaults already include argus-secure.com." />
                  <EnvVar name="APP_PUBLIC_URL" purpose="Canonical public URL for this deploy — added to the CORS allow-list and used in transactional emails." />
                  <EnvVar name="OWNER_PASSWORD" purpose="Override the seeded owner password on first boot. Always set this in production." />
                </tbody>
              </table>
            </div>

            <h3 className="font-display text-xl text-ink mt-6 mb-2">frontend/.env</h3>
            <div className="rounded-xl border border-line bg-panel p-4 overflow-x-auto" data-testid="docs-env-frontend">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-ink/45">
                    <th className="pb-2 pr-4">Variable</th>
                    <th className="pb-2 pr-4">Status</th>
                    <th className="pb-2">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <EnvVar name="REACT_APP_BACKEND_URL" required purpose="Absolute URL of the API. The frontend rewrites every fetch to {URL}/api/v1/..." />
                </tbody>
              </table>
            </div>
          </Section>

          {/* HEALTH */}
          <Section id="health" Icon={Shield} title="Health checks">
            <p>Two endpoints, two audiences.</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <code>GET /api/health</code> — fast, no-DB liveness probe. Returns 200 if the
                Python process is up. Suitable for load-balancer health checks.
              </li>
              <li>
                <code>GET /api/v1/health</code> — DB ping + integration audit. Reports which
                env vars are configured (presence only, never values) and the active CORS
                allow-list. Useful when debugging a production deploy.
              </li>
            </ul>
            <Code>{`curl -s $REACT_APP_BACKEND_URL/api/v1/health | jq
# {
#   "status": "ok",
#   "service": "argussecure-api",
#   "checks": {
#     "mongodb": { "ok": true, "detail": "argussecure" },
#     "integrations": { "EMERGENT_LLM_KEY": true, "STRIPE_API_KEY": true, ... },
#     "cors_allow_origins": ["https://argus-secure.com", ...]
#   }
# }`}</Code>
          </Section>

          {/* INTEGRATIONS */}
          <Section id="integrations" Icon={GitBranch} title="Integrations & where to get the keys">
            <ul className="list-none space-y-3 pl-0">
              <li className="flex items-start gap-3">
                <Bot size={16} className="text-gold mt-1 shrink-0" />
                <div>
                  <strong className="text-ink">Emergent LLM Key</strong> — powers Goddy +
                  Fix-It Agent (Claude Sonnet 4.5, GPT-4o, Gemini text + Nano Banana, etc).
                  Issued from your Emergent profile under <em>Universal Key</em>.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Cloud size={16} className="text-gold mt-1 shrink-0" />
                <div>
                  <strong className="text-ink">Stripe</strong> — test keys from
                  <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer" className="text-gold underline ml-1 inline-flex items-center gap-1">
                    Stripe dashboard <ExternalLink size={11} />
                  </a>. On Emergent, the platform proxy key works out of the box.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-gold mt-1 shrink-0" />
                <div>
                  <strong className="text-ink">Resend</strong> — get an API key at
                  <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-gold underline ml-1 inline-flex items-center gap-1">
                    resend.com/api-keys <ExternalLink size={11} />
                  </a>. Without it, mail still queues to <code>email_outbox</code> so you
                  can audit what would have been sent.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Shield size={16} className="text-gold mt-1 shrink-0" />
                <div>
                  <strong className="text-ink">hCaptcha</strong> — site + secret keys from
                  <a href="https://dashboard.hcaptcha.com" target="_blank" rel="noopener noreferrer" className="text-gold underline ml-1 inline-flex items-center gap-1">
                    hCaptcha dashboard <ExternalLink size={11} />
                  </a>.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Database size={16} className="text-gold mt-1 shrink-0" />
                <div>
                  <strong className="text-ink">OWASP ZAP</strong> — install the
                  daemon locally, then add a ZAP integration in
                  <Link to="/app/integrations" className="text-gold underline ml-1">Settings → Integrations</Link>.
                  Per-project <em>DAST engine</em> selector chooses between the built-in
                  scanner and ZAP.
                </div>
              </li>
            </ul>
          </Section>

          {/* PROD CHECKLIST */}
          <Section id="prod-checklist" Icon={CheckCircle2} title="Production checklist">
            <p>Run through this before pointing a real domain at ArgusSecure.</p>
            <ul className="space-y-2 text-sm">
              {[
                "Set OWNER_PASSWORD and JWT_SECRET to strong, unique values (32+ bytes).",
                "Rotate the seeded demo@argus.security and owner@argus.security passwords.",
                "Set CORS_ORIGINS to your exact domain(s) — drop the wildcard.",
                "Enable hCaptcha by setting both HCAPTCHA_SECRET and HCAPTCHA_SITE_KEY.",
                "Issue real Stripe + Resend keys (or accept the dev-mode degraded states).",
                "Confirm /api/v1/health returns status:ok with no missing integrations.",
                "Confirm /api/health is whitelisted in your load balancer / CDN.",
                "Verify the demo org appears at /demo (proves seeding ran cleanly).",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-safe mt-0.5 shrink-0" />
                  <span className="text-ink/80">{line}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* TROUBLESHOOTING */}
          <Section id="troubleshooting" Icon={Terminal} title="Troubleshooting">
            <h3 className="font-display text-xl text-ink mt-2 mb-1">Goddy says &quot;AI is disabled&quot;</h3>
            <p><code>EMERGENT_LLM_KEY</code> is unset or has zero balance. Add credit at <em>Profile → Universal Key → Add Balance</em>.</p>

            <h3 className="font-display text-xl text-ink mt-4 mb-1">Login returns CORS error from a custom domain</h3>
            <p>Add the domain to <code>CORS_ORIGINS</code> (or set <code>APP_PUBLIC_URL</code>). The defaults already cover <code>argus-secure.com</code> and the Emergent preview host.</p>

            <h3 className="font-display text-xl text-ink mt-4 mb-1">Scans hang in &quot;running&quot;</h3>
            <p>Check <code>/var/log/supervisor/backend.err.log</code> for the scanner traceback. For ZAP-engine projects, verify the ZAP daemon is reachable from the backend container.</p>

            <h3 className="font-display text-xl text-ink mt-4 mb-1">Verification emails never arrive</h3>
            <p>Without <code>RESEND_API_KEY</code>, mail is logged in the <code>email_outbox</code> collection. Owners can replay them by issuing the API key and re-running the relevant action.</p>
          </Section>

          {/* CTA */}
          <div className="rounded-xl border border-gold/30 bg-gold/5 p-6 mt-10" data-testid="docs-final-cta">
            <h3 className="font-display text-2xl mb-2">Stuck?</h3>
            <p className="text-ink/80 text-sm mb-4">
              The fastest path: open Goddy in the bottom-right and paste the exact error.
              Goddy knows the codebase. For human follow-up, email <a href="mailto:security@argus.security" className="text-gold underline">security@argus.security</a>.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/status" className="text-sm text-gold underline" data-testid="docs-link-status">System status →</Link>
              <Link to="/abuse" className="text-sm text-gold underline" data-testid="docs-link-abuse">Report abuse →</Link>
              <Link to="/legal/responsible-disclosure" className="text-sm text-gold underline" data-testid="docs-link-disclosure">Responsible disclosure →</Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
